import { Routes, RouterModule } from '@angular/router';
import { ModuleWithProviders } from '@angular/core';
import { PagesComponent } from './pages/pages.component';
import { NotFoundComponent } from './pages/errors/not-found/not-found.component';
import { ErrorComponent } from './pages/errors/error/error.component';
import { AuthGuard } from './core/guard';
import { NotAuthorizedComponent } from './pages/errors/not-authorized/not-authorized.component';
import { RoleEnum } from './core/enums';

export const routes: Routes = [
  {
    path: '',
    component: PagesComponent,
    children: [
      {
        path: '',
        pathMatch: 'prefix',
        loadChildren: () => import('./pages/home/home.module').then(m => m.HomeModule),
        data: {
          breadcrumb: 'MENU.HOME',
          roles: [RoleEnum.Admin, RoleEnum.Employee, RoleEnum.Provider],
        },
        canActivateChild: [AuthGuard],
      },
      {
        path: 'home',
        loadChildren: () => import('./pages/home/home.module').then(m => m.HomeModule),
        data: {
          breadcrumb: 'MENU.HOME',
          roles: [RoleEnum.Admin, RoleEnum.Employee, RoleEnum.Provider],
        },
        canActivateChild: [AuthGuard],
      },
      {
        path: 'dashboard',
        loadChildren: () =>
          import('./pages/dashboard/dashboard.module').then(m => m.DashboardModule),
        data: {
          breadcrumb: 'MENU.DASHBOARD',
          roles: [RoleEnum.Admin],
        },
        canActivateChild: [AuthGuard],
      },
      {
        path: 'employees',
        loadChildren: () => import('./pages/employees/employee.module').then(m => m.EmployeeModule),
        data: {
          breadcrumb: 'MENU.EMPLOYEES',
          roles: [RoleEnum.Admin, RoleEnum.Employee],
        },
        canActivateChild: [AuthGuard],
      },
      {
        path: 'settings',
        loadChildren: () =>
          import('./pages/application-settings/application-settings.module').then(
            m => m.ApplicationSettingsModule
          ),
        data: {
          breadcrumb: 'MENU.APPLICATION_SETTINGS',
          roles: [RoleEnum.Admin, RoleEnum.Employee],
        },
        canActivateChild: [AuthGuard],
      },
      {
        path: 'providers',
        loadChildren: () => import('./pages/providers/provider.module').then(m => m.ProviderModule),
        data: {
          breadcrumb: 'MENU.PROVIDERS',
          roles: [RoleEnum.Admin, RoleEnum.Employee],
        },
        canActivateChild: [AuthGuard],
      },
      {
        path: 'citizens',
        loadChildren: () => import('./pages/citizens/citizen.module').then(m => m.CitizenModule),
        data: {
          breadcrumb: 'MENU.CITIZENS',
          roles: [RoleEnum.Admin, RoleEnum.Employee, RoleEnum.Provider],
        },
        canActivateChild: [AuthGuard],
      },
      {
        path: 'profile',
        loadChildren: () => import('./pages/profile/profile.module').then(m => m.ProfileModule),
        data: {
          breadcrumb: 'MENU.PROFILE',
          roles: [RoleEnum.Provider],
        },
        canActivateChild: [AuthGuard],
      },
      {
        path: 'fields',
        loadChildren: () => import('./pages/fields/fields.module').then(m => m.FieldsModule),
        data: {
          breadcrumb: 'MENU.FIELDS',
          roles: [RoleEnum.Admin, RoleEnum.Employee, RoleEnum.Provider],
        },
        canActivateChild: [AuthGuard],
      },
      {
        path: 'regions',
        loadChildren: () => import('./pages/regions/regions.module').then(m => m.RegionsModule),
        data: {
          breadcrumb: 'MENU.REGIONS',
          roles: [RoleEnum.Admin, RoleEnum.Employee],
        },
        canActivateChild: [AuthGuard],
      },
      {
        path: 'field-size',
        loadChildren: () =>
          import('./pages/field-size/field-size.module').then(m => m.FieldSizeModule),
        data: {
          breadcrumb: 'MENU.FIELDS_SIZES',
          roles: [RoleEnum.Admin, RoleEnum.Employee],
        },
        canActivateChild: [AuthGuard],
      },
      {
        path: 'field-type',
        loadChildren: () =>
          import('./pages/field-type/field-type.module').then(m => m.FieldTypeModule),
        data: {
          breadcrumb: 'MENU.FIELDS_TYPES',
          roles: [RoleEnum.Admin, RoleEnum.Employee],
        },
        canActivateChild: [AuthGuard],
      },
      {
        path: 'panels',
        loadChildren: () =>
          import('./pages/advertisements/advertisements.module').then(m => m.AdvertisementsModule),
        data: {
          breadcrumb: 'MENU.ADS',
          roles: [RoleEnum.Admin, RoleEnum.Employee],
        },
        canActivateChild: [AuthGuard],
      },
      {
        path: 'academies',
        loadChildren: () =>
          import('./pages/academies/academies.module').then(m => m.AcademiesModule),
        data: {
          breadcrumb: 'MENU.PROGRAMS',
          roles: [RoleEnum.Admin, RoleEnum.Provider],
        },
        canActivateChild: [AuthGuard],
      },
      {
        path: 'reservations',
        loadChildren: () =>
          import('./pages/reservations/reservations.module').then(m => m.ReservationsModule),
        data: {
          breadcrumb: 'MENU.RESERVATIONS',
          roles: [RoleEnum.Admin, RoleEnum.Provider],
        },
        canActivateChild: [AuthGuard],
      },
      {
        path: 'program-sports',
        loadChildren: () =>
          import('./pages/program-sport/program-sport.module').then(m => m.ProgramSportModule),
        data: {
          breadcrumb: 'MENU.PROGRAM_SPORTS',
          roles: [RoleEnum.Admin],
        },
        canActivateChild: [AuthGuard],
      },
      {
        path: 'program-reservations',
        loadChildren: () =>
          import('./pages/program-reservations/program-reservations.module').then(
            m => m.ProgramReservationsModule
          ),
        data: {
          breadcrumb: 'MENU.PROGRAM_RESERVATIONS',
          roles: [RoleEnum.Admin, RoleEnum.Employee, RoleEnum.Provider],
        },
        canActivateChild: [AuthGuard],
      },
      {
        path: 'closed-slots',
        loadChildren: () =>
          import('./pages/closed-slots/closed-slots.module').then(m => m.ClosedSlotsModule),
        data: {
          breadcrumb: 'MENU.CLOSED_SLOTS',
          roles: [RoleEnum.Provider, RoleEnum.Admin],
        },
        canActivateChild: [AuthGuard],
      },

      {
        path: 'schedule',
        loadChildren: () => import('./pages/schedule/schedule.module').then(m => m.ScheduleModule),
        data: {
          breadcrumb: 'MENU.SCHEDULE',
          roles: [RoleEnum.Provider, RoleEnum.Admin],
        },
        canActivateChild: [AuthGuard],
      },
      {
        path: 'branches',
        loadChildren: () => import('./pages/branch/branch.module').then(m => m.BranchesModule),
        data: {
          breadcrumb: 'MENU.BRANCHES',
          roles: [RoleEnum.Admin, RoleEnum.Provider, RoleEnum.Employee],
        },
        canActivateChild: [AuthGuard],
      },
    ],
  },
  {
    path: 'login',
    loadChildren: () => import('./pages/login/login.module').then(m => m.LoginModule),
  },
  {
    path: 'register',
    loadChildren: () => import('./pages/register/register.module').then(m => m.RegisterModule),
  },
  {
    path: 'error',
    component: ErrorComponent,
    data: { breadcrumb: 'MENU.ERROR' },
  },
  { path: 'not-authorized', component: NotAuthorizedComponent },
  { path: '**', component: NotFoundComponent },
];

export const routing: ModuleWithProviders = RouterModule.forRoot(routes, {
  useHash: true,
});
